import defaultProperties from '@/service/entity/default-properties.js';

const padding = 125;
// a 'typical' node is width 160 and height 80
const nodeGeometry = {width: 160 + padding, height: 80 + padding, padding: padding};

const convert = (model, nodes) => {
    const zones = [];

    // there may be no diagrams, or no cells within a diagram, or no cells that are boundary boxes
    model.detail.diagrams?.forEach((diagram) => {
        diagram.cells?.forEach((cell) => {
            // from a TM-BOM point of view, trust boundary curves are also trust zones
            if (cell.data.isTrustBoundary) {
                zones.push({
                    symbolic_name: cell.id,
                    title: cell.data.name || 'Trust zone generated by export from Threat Dragon',
                    description: cell.data.description
                });
                if (cell.data.containedElements) {
                    // containedElements is an array of node ids that are contained in the trust zone
                    for (const element of cell.data.containedElements) {
                        let index = nodes.actors.findIndex((x) => x.symbolic_name === element);
                        if (index >= 0) {
                            nodes.actors[index].trust_zone = cell.id;
                            continue;
                        }
                        index = nodes.components.findIndex((x) => x.symbolic_name === element);
                        if (index >= 0) {
                            nodes.components[index].trust_zone = cell.id;
                            continue;
                        }
                        index = nodes.data_stores.findIndex((x) => x.symbolic_name === element);
                        if (index >= 0) {
                            nodes.data_stores[index].trust_zone = cell.id;
                        }
                    }
                }
            }
        });
    });

    return zones;
};

const countNodes = (model, trustZone) => {
    let count = 0;
    const zones = [];
    model.trust_zones?.forEach((zone) => zones.push(zone.symbolic_name));

    model.actors?.forEach((actor) => {
        if (actor.trust_zone === trustZone || (!zones.includes(actor.trust_zone) && trustZone === undefined)) {
            count++;
        }
    });

    model.components?.forEach((component) => {
        if (component.trust_zone === trustZone || (!zones.includes(component.trust_zone) && trustZone === undefined)) {
            count++;
        }
    });

    model.data_stores?.forEach((data_store) => {
        if (data_store.trust_zone === trustZone || (!zones.includes(data_store.trust_zone) && trustZone === undefined)) {
            count++;
        }
    });

    return count;
};

const dimensionZone = (model, trustZone) => {
    const nodeCount = countNodes(model, trustZone);
    let nodesPerSide = 1;

    // the nodes are placed around the top and lhs edges of the trust boundary box
    // so that 0 or 1 component is side of 1, 2 & 3 is side of 2
    // 4 & 5 is side of 3, 6 & 7 is side of 4, 8 & 9 is side of 5 and so on
    if (nodeCount > 1) {
        nodesPerSide = Math.floor((nodeCount + 2) / 2);
    }

    return { 'width': nodesPerSide * nodeGeometry.width, 'height': nodesPerSide * nodeGeometry.height };
};

const merge = (model) => {
    const boundaryBoxes = [];
    const position = {x: nodeGeometry.padding, y: nodeGeometry.padding};

    // nodes not in any trust zone are in a notional 'public' trust zone
    if (countNodes(model, undefined)) {
        // make room for nodes round edge of diagram
        position.x += nodeGeometry.width;
        position.y += nodeGeometry.height;
    }
    const offset = {column: position.x, row: position.y};

    if (model.trust_zones) {
        let boxCount = 1;
        let zIndex = -1;

        model.trust_zones.forEach((zone) => {
            const dimensions = dimensionZone(model, zone.symbolic_name);
            const data = defaultProperties.defaultData('tm.BoundaryBox');
            data.name = zone.title;
            data.description = zone.description;

            boundaryBoxes.push({
                position: {
                    x: position.x,
                    y: position.y
                },
                size: dimensions,
                attrs: { label: { text: zone.title } },
                shape: 'trust-boundary-box',
                id: zone.symbolic_name,
                zIndex: zIndex--,
                data: data
            });

            // track next trust boundary box row
            offset.row = Math.max(offset.row, (position.y + dimensions.height + nodeGeometry.padding));

            if (boxCount++ % 2) {
                // ready for next trust boundary box in the row
                position.x += (dimensions.width + nodeGeometry.padding);
            } else {
                // start a new row
                position.x = offset.column;
                position.y = offset.row;
            }
        });
    }

    return boundaryBoxes;
};

export default {
    convert,
    merge,
    nodeGeometry
};
